import { useCallback, useEffect, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import {
    Alert, Box, Button, CircularProgress, FormControl, InputLabel,
    MenuItem, Pagination, Rating, Select, Stack, Typography
} from '@mui/material';

import {
    deleteCustomerReview,
    getPublicProductReviews,
    getReviewEligibility,
    reviewErrorText
} from '../../../../actions/review';
import { msg } from '../../../../actions/swal_msg';
import ProductReview from './ProductReview';
import ReviewModal from './ReviewModal';

const EMPTY_SUMMARY = {
    average: 0,
    count: 0,
    distribution: {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0
    }
};

const initialEligibility = {
    can_review: false,
    reason: 'loading',
    existing_review: null
};

const ProductReviewsSection = ({
    accountId,
    onSummaryChange,
    productId,
    signupReturnPath,
    variant
}) => {
    const [reviews, setReviews] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 0,
        total: 0
    });
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState('newest');
    const [variantOnly, setVariantOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [eligibility, setEligibility] = useState(initialEligibility);
    const [modal, setModal] = useState({
        open: false,
        action: 'add',
        review: null
    });

    const loadReviews = useCallback(async () => {
        if (!productId)
            return;

        setLoading(true);
        setError('');

        try {
            const body = await getPublicProductReviews(productId, {
                page,
                limit: 10,
                sort,
                variantId: variantOnly ? variant._id : ''
            });

            if (!body.status) {
                setError(reviewErrorText(body.msg));
                return;
            }

            setReviews(body.resp.reviews);
            setSummary(body.resp.summary || EMPTY_SUMMARY);
            setPagination(body.resp.pagination);
        } catch (requestError) {
            setError('No se pudieron cargar las reseñas.');
        } finally {
            setLoading(false);
        }
    }, [page, productId, sort, variant._id, variantOnly]);

    const loadEligibility = useCallback(async () => {
        if (!productId || !variant._id || accountId === '0') {
            setEligibility({
                ...initialEligibility,
                reason: accountId === '0' ? 'login_required' : 'loading'
            });
            return;
        }

        try {
            const body = await getReviewEligibility(productId, variant._id);

            if (!body.status) {
                setEligibility({
                    ...initialEligibility,
                    reason: body.msg || 'request-err'
                });
                return;
            }

            setEligibility(body.resp);
        } catch (requestError) {
            setEligibility({
                ...initialEligibility,
                reason: 'request-err'
            });
        }
    }, [accountId, productId, variant._id]);

    useEffect(() => {
        setPage(1);
    }, [productId, sort, variant._id, variantOnly]);

    useEffect(() => {
        loadReviews();
    }, [loadReviews]);

    useEffect(() => {
        loadEligibility();
    }, [loadEligibility]);

    const handleSaved = async response => {
        if (response.summary)
            onSummaryChange(response.summary);

        setPage(1);
        await Promise.all([
            loadReviews(),
            loadEligibility()
        ]);
    };

    const handleDelete = async review => {
        try {
            const body = await deleteCustomerReview(review.id);

            if (!body.status) {
                msg('warning', 'Reseña', reviewErrorText(body.msg));
                return;
            }

            if (body.resp.summary)
                onSummaryChange(body.resp.summary);

            msg('success', 'Reseña eliminada',
                'Tu reseña dejó de mostrarse en la tienda');
            setPage(1);
            await Promise.all([
                loadReviews(),
                loadEligibility()
            ]);
        } catch (requestError) {
            msg('error', 'Reseña', 'No se pudo eliminar la reseña');
        }
    };

    const renderCustomerAction = () => {
        if (accountId === '0') {
            return (
                <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                        Inicia sesión para escribir una reseña de una compra entregada.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to="/signup"
                        state={{ page: signupReturnPath }}
                        variant="contained"
                    >
                        Registrarme
                    </Button>
                </Stack>
            );
        }

        if (eligibility.reason === 'loading')
            return <CircularProgress size={24} />;

        if (eligibility.can_review) {
            return (
                <Button
                    variant="contained"
                    onClick={() => setModal({
                        open: true,
                        action: 'add',
                        review: null
                    })}
                >
                    Escribir reseña
                </Button>
            );
        }

        if (
            eligibility.reason === 'already_reviewed' &&
            eligibility.existing_review
        ) {
            return (
                <Button
                    variant="outlined"
                    onClick={() => setModal({
                        open: true,
                        action: 'edit',
                        review: eligibility.existing_review
                    })}
                >
                    Editar tu reseña
                </Button>
            );
        }

        return (
            <Typography variant="body2" color="text.secondary">
                {reviewErrorText(eligibility.reason)}
            </Typography>
        );
    };

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
            >
                <Box>
                    <Typography variant="h5">Reseñas de clientes</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Rating
                            precision={0.1}
                            value={summary.average || 0}
                            readOnly
                        />
                        <Typography variant="body2" color="text.secondary">
                            {summary.count > 0 ?
                                `${summary.average} de 5 · ${summary.count} reseñas`
                            :
                                'Este producto todavía no tiene reseñas'}
                        </Typography>
                    </Stack>
                </Box>
                {renderCustomerAction()}
            </Stack>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mt: 3 }}
            >
                <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>Mostrar</InputLabel>
                    <Select
                        label="Mostrar"
                        value={variantOnly ? 'variant' : 'all'}
                        onChange={({ target }) => setVariantOnly(
                            target.value === 'variant'
                        )}
                    >
                        <MenuItem value="all">Todas las variantes</MenuItem>
                        <MenuItem value="variant">
                            Esta variante
                        </MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>Ordenar</InputLabel>
                    <Select
                        label="Ordenar"
                        value={sort}
                        onChange={({ target }) => setSort(target.value)}
                    >
                        <MenuItem value="newest">Más recientes</MenuItem>
                        <MenuItem value="oldest">Más antiguas</MenuItem>
                        <MenuItem value="highest">Mayor calificación</MenuItem>
                        <MenuItem value="lowest">Menor calificación</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {loading ?
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            :
                <>
                    {reviews.map(review =>
                        <ProductReview
                            key={review.id}
                            review={review}
                            isOwner={
                                eligibility.existing_review &&
                                eligibility.existing_review.id === review.id
                            }
                            onEdit={() => setModal({
                                open: true,
                                action: 'edit',
                                review: eligibility.existing_review
                            })}
                            onDelete={handleDelete}
                        />
                    )}
                    {!error && reviews.length < 1 &&
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                            No hay reseñas para mostrar con este filtro.
                        </Typography>
                    }
                </>
            }

            {pagination.pages > 1 &&
                <Stack alignItems="center" sx={{ mt: 3 }}>
                    <Pagination
                        count={pagination.pages}
                        page={page}
                        onChange={(event, value) => setPage(value)}
                    />
                </Stack>
            }

            <ReviewModal
                action={modal.action}
                open={modal.open}
                productId={productId}
                review={modal.review}
                variant={variant}
                onClose={() => setModal(current => ({
                    ...current,
                    open: false
                }))}
                onSaved={handleSaved}
            />
        </Box>
    );
};

export default ProductReviewsSection;
